import moment from 'moment'
import { VAT_CODE_DEFAULTS, VAT_CODES } from '@/enums/vat'
import { stringToMoment } from '@/utils/date'

const reduceListDuplicates = (listOfvalues) => listOfvalues.filter((v, i) => listOfvalues.indexOf(v) === i)
const extractVatByCode = (code, listOfVatPercentages, vatList) => {
  const percentages = reduceListDuplicates(listOfVatPercentages)
  const listOfVats = percentages.map(value => vatList.find(({ value: vatValue }) => parseFloat(vatValue) === value))
  return listOfVats.filter(({ vatCode }) => vatCode === code).pop()
}

export const vatDatePeriodisValid = (dateAsDate, validFrom, validTo) => {
  const dateAsMoment = dateAsDate ? moment(new Date(dateAsDate)) : moment()

  let isValid = false

  if (validFrom && validTo) {
    isValid = dateAsMoment.isBetween(moment(validFrom), moment(validTo), undefined, '[]')
  } else if (validFrom) {
    isValid = dateAsMoment.isSameOrAfter(moment(validFrom))
  }

  return isValid
}

export const vatPercentExists = (vatList, vatPercent, user = null) => {
  if (vatList == null) {
    throw new Error('vatPercentExists - param vatList is not set.')
  }
  if (vatPercent == null) {
    throw new Error('vatPercentExists - param vatPercent is not set.')
  }

  const result = {
    isValid: true,
    fallbackVatRate: null,
  }

  if (vatList.findIndex(v => v.value === vatPercent) >= 0) {
    return result
  }

  const fallbackDefaultVatRate = vatList.find(v => v.isCurrent && v.isDefault)?.value || 0
  result.isValid = false
  result.fallbackVatRate = fallbackDefaultVatRate

  if (user?.getVatList) {
    const allVats = user.getVatList()
    const vatType = getVatTypeForValue(vatPercent, allVats)
    const fallback = vatList.find(v => v.isCurrent && v.vatCode === vatType)?.value || -1
    if (fallback !== -1) {
      result.fallbackVatRate = fallback
    }
  }

  return result
}

export const findVatRateForPeriod = (validVatList, allVatList, vatPercent) => {
  const selectedValue = parseFloat(vatPercent)
  if (isNaN(selectedValue) || !validVatList?.length) return null

  const isSelectedRate = vat => parseFloat(vat.value) === selectedValue
  if (validVatList.some(isSelectedRate)) return null

  const selectedVatCode = allVatList?.find(isSelectedRate)?.vatCode
  if (!selectedVatCode) return null

  const replacement = validVatList.find(vat => vat.vatCode === selectedVatCode)

  return replacement ? replacement.value : null
}

export const filterVatListByDateAndCountry = (vatList, dateAsDate, expectedCountryCode) => {
  const filtered = vatList
    .filter(({ countryCode }) => countryCode === expectedCountryCode)
    .filter(({ validFrom, validTo }) => vatDatePeriodisValid(dateAsDate, validFrom, validTo))

  return filtered
}

export const extractStandardVat = (listOfVatPercentages, vatList) => {
  return extractVatByCode(VAT_CODE_DEFAULTS.STANDARD, listOfVatPercentages, vatList)
}

export const extractReducedVat = (listOfVatPercentages, vatList) => {
  return extractVatByCode(VAT_CODE_DEFAULTS.REDUCED, listOfVatPercentages, vatList)
}

export const isVat1 = (percentValue, listOfVats) => {
  return listOfVats
    .filter(({ value, vatCode }) => parseFloat(value) === parseFloat(percentValue) && vatCode === VAT_CODE_DEFAULTS.STANDARD)
    .length > 0
}
export const isVat2 = (percentValue, listOfVats) => {
  return listOfVats
    .filter(({ value, vatCode }) => parseFloat(value) === parseFloat(percentValue) && vatCode === VAT_CODE_DEFAULTS.REDUCED)
    .length > 0
}

export const dateAsStringToUtc = (dateAsString) => {
  const dateAsMoment = stringToMoment(dateAsString)
  return Date.UTC(dateAsMoment.year(), dateAsMoment.month(), dateAsMoment.date())
}

export const vatCodesForUserByDate = (dateAsString, { user }) => {
  const lookupDate = dateAsStringToUtc(dateAsString)
  return user.getVatList(lookupDate)
}

export const groupAccountsByVat = (vatGroups) => {
  const grouped = vatGroups.reduce((result, vatGroup) => {
    result[vatGroup.key.toUpperCase()] = vatGroup.accounts
    return result
  }, {})

  return grouped
}

export const getVatTypeForValue = (value, vatList) => {
  let vatType = VAT_CODES.VAT0
  if (isVat1(value, vatList)) {
    vatType = VAT_CODES.VAT1
  }
  if (isVat2(value, vatList)) {
    vatType = VAT_CODES.VAT2
  }
  return vatType
}

export const sortVatListByDefault = (vatList) => {
  vatList.sort((a, b) => {
    // sort descending by current country-code, and then by percent-value
    if (a.isCurrent !== b.isCurrent) {
      return b.isCurrent ? 1 : -1
    }
    return b.value - a.value
  })

  return vatList
}
